import {
    getFirestore,
    collection,
    addDoc,
    serverTimestamp,
    query,
    where,
    getDocs,
    updateDoc,
    doc,
} from "firebase/firestore";
import firebaseApp from "../firebase/credenciales";

const db = getFirestore(firebaseApp);

// Tipos de notificaciones
export const TIPOS_NOTIFICACION = {
    NUEVA_SOLICITUD: "nueva_solicitud",
    SOLICITUD_APROBADA: "solicitud_aprobada",
    SOLICITUD_RECHAZADA: "solicitud_rechazada",
    VACACIONES_POR_VENCER: "vacaciones_por_vencer",
    AUSENCIA_PROXIMA: "ausencia_proxima",
};

// Prioridades
export const PRIORIDADES = {
    BAJA: "baja",
    MEDIA: "media",
    ALTA: "alta",
    URGENTE: "urgente",
};

/**
 * Notificar a administradores sobre una nueva solicitud de ausencia
 */
export const notificarNuevaSolicitud = async (
    solicitudData,
    rucEmpresa
) => {
    try {
        // Obtener todos los administradores de la empresa
        const usersRef = collection(db, "users");
        const qAdmins = query(
            usersRef,
            where("rucEmpresa", "==", rucEmpresa),
            where("rol", "in", ["admin", "superAdmin"])
        );

        const adminsSnapshot = await getDocs(qAdmins);

        // Crear notificación para cada administrador
        const notificaciones = adminsSnapshot.docs.map(async (adminDoc) => {
            const adminData = adminDoc.data();
            const notifRef = collection(
                db,
                `notificaciones/${adminData.dni}/inbox`
            );

            return addDoc(notifRef, {
                tipo: TIPOS_NOTIFICACION.NUEVA_SOLICITUD,
                prioridad: PRIORIDADES.MEDIA,
                titulo: "Nueva Solicitud de Ausencia",
                mensaje: `${solicitudData.empleadoNombre} ha solicitado una ausencia de tipo ${solicitudData.tipoAusencia}`,
                datos: {
                    solicitudId: solicitudData.id,
                    empleadoDNI: solicitudData.empleadoDNI,
                    empleadoNombre: solicitudData.empleadoNombre,
                    tipoAusencia: solicitudData.tipoAusencia,
                    fechaInicio: solicitudData.fechaInicio,
                    fechaFin: solicitudData.fechaFin,
                },
                leida: false,
                rucEmpresa: rucEmpresa,
                createdAt: serverTimestamp(),
            });
        });

        await Promise.all(notificaciones);
        console.log("✅ Notificaciones de nueva solicitud enviadas");
    } catch (error) {
        console.error("Error al notificar nueva solicitud:", error);
        throw error;
    }
};

/**
 * Notificar al empleado sobre la aprobación de su solicitud
 */
export const notificarAprobacion = async (
    empleadoDNI,
    empleadoNombre,
    tipoAusencia,
    aprobadoPor,
    rucEmpresa
) => {
    try {
        const notifRef = collection(db, `notificaciones/${empleadoDNI}/inbox`);

        await addDoc(notifRef, {
            tipo: TIPOS_NOTIFICACION.SOLICITUD_APROBADA,
            prioridad: PRIORIDADES.ALTA,
            titulo: "Solicitud Aprobada",
            mensaje: `Tu solicitud de ${tipoAusencia} ha sido aprobada por ${aprobadoPor}`,
            datos: {
                tipoAusencia,
                aprobadoPor,
            },
            leida: false,
            rucEmpresa: rucEmpresa,
            createdAt: serverTimestamp(),
        });

        console.log(`✅ Notificación de aprobación enviada a ${empleadoNombre}`);
    } catch (error) {
        console.error("Error al notificar aprobación:", error);
        throw error;
    }
};

/**
 * Notificar al empleado sobre el rechazo de su solicitud
 */
export const notificarRechazo = async (
    empleadoDNI,
    empleadoNombre,
    tipoAusencia,
    rechazadoPor,
    motivo,
    rucEmpresa
) => {
    try {
        const notifRef = collection(db, `notificaciones/${empleadoDNI}/inbox`);

        await addDoc(notifRef, {
            tipo: TIPOS_NOTIFICACION.SOLICITUD_RECHAZADA,
            prioridad: PRIORIDADES.ALTA,
            titulo: "Solicitud Rechazada",
            mensaje: `Tu solicitud de ${tipoAusencia} ha sido rechazada. Motivo: ${motivo}`,
            datos: {
                tipoAusencia,
                rechazadoPor,
                motivo,
            },
            leida: false,
            rucEmpresa: rucEmpresa,
            createdAt: serverTimestamp(),
        });

        console.log(`✅ Notificación de rechazo enviada a ${empleadoNombre}`);
    } catch (error) {
        console.error("Error al notificar rechazo:", error);
        throw error;
    }
};

/**
 * Notificar sobre vacaciones por vencer
 */
export const notificarVacacionesPorVencer = async (
    empleadoDNI,
    empleadoNombre,
    diasPendientes,
    fechaVencimiento,
    rucEmpresa
) => {
    try {
        const notifRef = collection(db, `notificaciones/${empleadoDNI}/inbox`);

        const diasRestantes = Math.ceil(
            (new Date(fechaVencimiento) - new Date()) / (1000 * 60 * 60 * 24)
        );

        let prioridad = PRIORIDADES.BAJA;
        if (diasRestantes <= 30) prioridad = PRIORIDADES.URGENTE;
        else if (diasRestantes <= 60) prioridad = PRIORIDADES.ALTA;
        else if (diasRestantes <= 90) prioridad = PRIORIDADES.MEDIA;

        await addDoc(notifRef, {
            tipo: TIPOS_NOTIFICACION.VACACIONES_POR_VENCER,
            prioridad: prioridad,
            titulo: "Vacaciones por Vencer",
            mensaje: `Tienes ${diasPendientes} días de vacaciones que vencen en ${diasRestantes} días. Programa tus vacaciones pronto.`,
            datos: {
                diasPendientes,
                fechaVencimiento,
                diasRestantes,
            },
            leida: false,
            rucEmpresa: rucEmpresa,
            createdAt: serverTimestamp(),
        });

        console.log(
            `✅ Notificación de vacaciones por vencer enviada a ${empleadoNombre}`
        );
    } catch (error) {
        console.error("Error al notificar vacaciones por vencer:", error);
        throw error;
    }
};

/**
 * Notificar sobre ausencia próxima
 */
export const notificarAusenciaProxima = async (
    empleadoDNI,
    empleadoNombre,
    tipoAusencia,
    fechaInicio,
    rucEmpresa
) => {
    try {
        const notifRef = collection(db, `notificaciones/${empleadoDNI}/inbox`);

        await addDoc(notifRef, {
            tipo: TIPOS_NOTIFICACION.AUSENCIA_PROXIMA,
            prioridad: PRIORIDADES.MEDIA,
            titulo: "Recordatorio de Ausencia",
            mensaje: `Recuerda que tienes programada una ausencia de tipo ${tipoAusencia} desde ${new Date(
                fechaInicio
            ).toLocaleDateString("es-PE")}`,
            datos: {
                tipoAusencia,
                fechaInicio,
            },
            leida: false,
            rucEmpresa: rucEmpresa,
            createdAt: serverTimestamp(),
        });

        console.log(
            `✅ Notificación de ausencia próxima enviada a ${empleadoNombre}`
        );
    } catch (error) {
        console.error("Error al notificar ausencia próxima:", error);
        throw error;
    }
};

/**
 * Marcar notificación como leída
 */
export const marcarComoLeida = async (empleadoDNI, notificacionId) => {
    try {
        const notifRef = doc(
            db,
            `notificaciones/${empleadoDNI}/inbox`,
            notificacionId
        );

        await updateDoc(notifRef, {
            leida: true,
            fechaLectura: serverTimestamp(),
        });

        console.log("✅ Notificación marcada como leída");
    } catch (error) {
        console.error("Error al marcar notificación como leída:", error);
        throw error;
    }
};

/**
 * Obtener notificaciones no leídas de un empleado
 */
export const obtenerNotificacionesNoLeidas = async (empleadoDNI) => {
    try {
        const notifRef = collection(db, `notificaciones/${empleadoDNI}/inbox`);
        const q = query(notifRef, where("leida", "==", false));

        const querySnapshot = await getDocs(q);

        return querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
        }));
    } catch (error) {
        console.error("Error al obtener notificaciones no leídas:", error);
        throw error;
    }
};
